/**
 * promptGenerator.js
 * 
 * INC schema lookup utilities.
 * Maps Noun/Modifier → ordered properties from INC file data.
 * 
 * NOTE: No static prompt templates here. All prompts are dynamically
 * generated via the Property Prompt Configuration (backend merge).
 */

const normalize = (value) => String(value ?? '').trim();

const parseSeq = (value) => {
    const normalized = normalize(value);
    const numeric = Number(normalized);
    if (Number.isFinite(numeric)) {
        return { isNumeric: true, numeric, text: '' };
    }
    return { isNumeric: false, numeric: Number.POSITIVE_INFINITY, text: normalized.toLowerCase() };
};

const sortIncRowsBySeq = (a, b) => {
    const seqA = parseSeq(a.seq);
    const seqB = parseSeq(b.seq);

    if (seqA.isNumeric && seqB.isNumeric) {
        return seqA.numeric - seqB.numeric;
    }

    if (seqA.isNumeric && !seqB.isNumeric) {
        return -1;
    }

    if (!seqA.isNumeric && seqB.isNumeric) {
        return 1;
    }

    return seqA.text.localeCompare(seqB.text);
};

/**
 * Build a lookup map: ESCN (noun/modifier) → [rows...]
 */
export const buildIncLookup = (incRows = []) => {
    const lookup = new Map();

    incRows.forEach((row) => {
        const escn = normalize(row.escn).toUpperCase();
        if (!escn) return;

        if (!lookup.has(escn)) {
            lookup.set(escn, []);
        }

        lookup.get(escn).push(row);
    });

    return lookup;
};

/**
 * Get ordered property names for a given Noun/Modifier from INC data.
 * Returns string[] of property names sorted by sequence.
 */
export const getOrderedProperties = (nounModifier, incLookup) => {
    const nounModifierKey = normalize(nounModifier).toUpperCase();
    if (!nounModifierKey) return [];

    const matches = incLookup.get(nounModifierKey) || [];
    const seen = new Set();
    return [...matches]
        .sort(sortIncRowsBySeq)
        .map((row) => normalize(row.property))
        .filter((property) => {
            if (!property) return false;
            // INC files can contain the same schema property many times for one
            // ESCN. Preserve the first (sequence-ordered) occurrence only.
            const key = property.toUpperCase().replace(/\s+/g, ' ');
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};
